<template>
    <div class="toggle"
         :class="classes">
        <input :id="name"
               type="checkbox"
               :name="name"
               :disabled="disabled"
               :checked="modelValue"
               @change="$emit('update:modelValue', $event.target.checked)" />
        <label :for="name"><slot></slot>{{ label ? l(label) : '' }}</label>
    </div>
</template>

<script>
export default {
    props: {
        disabled: {
            type: Boolean,
            default: false,
        },

        name: {
            type: String,
            required: true,
        },

        size: {
            type: String,
            default: null,
        },

        borderless: {
            type: Boolean,
            default: false,
        },

        label: {
            type: String,
            default: '',
        },

        labelPosition: {
            type: String,
            default: 'before',
        },

        modelValue: {
            type: Boolean,
            default: false,
        },

        /**
         * Use the readonly prop if this toggle is only going to be displaying
         * a value controlled externally and clicking it should not do anything.
         */
        readOnly: {
            type: Boolean,
            default: false,
        },
    },

    emits: [
        'update:modelValue',
    ],

    computed: {
        classes() {
            return [
                this.disabled ? 'toggle--disabled' : '',
                this.labelPosition === 'before' ? 'toggle--label-before' : '',
                this.labelPosition === 'after' ? 'toggle--label-after' : '',
                this.size ? `toggle--${this.size}` : '',
                this.borderless ? 'toggle--borderless' : '',
                this.readOnly ? 'toggle--readonly' : '',
            ];
        },
    },
};
</script>

<style lang="less">
@import (less, reference) "../../../less/variables.less";
@import (less, reference) "../../../less/vendor/fontawesome.less";

.toggle {
    position: relative;
    height: 30px;

    input {
        opacity: 0; // Hide the checkbox
        position: absolute !important;
        max-width: none;
    }

    input + label {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        user-select: none;
        transition: .4s ease;
        -webkit-tap-highlight-color: transparent;
        height: 30px;
        min-width: 50px;
        max-width: none;
        margin: 0;
        font-size: @font-size-base;
        cursor: pointer;

        &:not(:empty) {
            // Add right padding so there can be text in the label
            // and the toggle slider can be on the right of the text.
            padding-right: 60px;
        }

        &:before,
        &:after {
            content: "";
            position: absolute;
            display: block;
        }

        // The track that the toggle "dot" travels within.
        &:before {
            background: white;
            height: 30px;
            width: 51px;
            top: 0;
            border-radius: 30px;
            border: 1px solid #e4e4e4;
            transition: .2s cubic-bezier(.24, 0, .5, 1);
        }

        // The toggle "dot".
        &:after {
            background: whitesmoke;
            height: 28px;
            width: 28px;
            top: 1px;
            right: 54px - 30px;
            border-radius: 60px;
            box-shadow: 0 0 0 1px hsla(0, 0%, 0%, 0.1), 0 4px 0 0 hsla(0, 0%, 0%, .04), 0 4px 9px hsla(0, 0%, 0%, .13), 0 3px 3px hsla(0, 0%, 0%, .05);
            transition: .35s cubic-bezier(.54, 1.60, .5, 1);
        }
    }

    &--borderless {
        input + label {
            &:before {
                // Remove the white border around the toggle.
                border: none;
            }
        }
    }

    &--readonly {
        pointer-events: none;
    }

    &--label-before {
        input + label {
            justify-content: flex-end;
            //padding-left: 60px;

            &:not(:empty) {
                // Add padding between the label and the slider.
                // This is the width of the slider plus a gutter.
                padding-right: 60px;
            }

            // The track that the toggle "dot" travels within.
            // Positioned at the right end of the element.
            &:before {
                right: 0;
            }

            // The toggle "dot".
            // Positioned at the left end of the track (disabled), at the right end of the element.
            &:after {
                right: 54px - 30px;
            }
        }
    }

    &--text-left {
        input + label {
            justify-content: flex-start;
        }
    }

    &--label-after {
        input + label {
            justify-content: flex-start;
            padding-right: 60px;

            &:not(:empty) {
                // Add padding so the label text doesn't cover the slider.
                padding-left: 60px;
            }

            // The track that the toggle "dot" travels within.
            // Positioned at the left end of the element.
            &:before {
                left: 0;
            }

            // The toggle "dot".
            // Positioned at the left end of the track (disabled) at the left end of the element.
            &:after {
                left: 0;
            }
        }
    }

    // When Active
    input:checked + label {
        // The track that the toggle "dot" travels within.
        &:before {
            background: @brand-success; // Active Color
            -moz-transition: width .2s cubic-bezier(0, 0, 0, .1);
            -o-transition: width .2s cubic-bezier(0, 0, 0, .1);
            -webkit-transition: width .2s cubic-bezier(0, 0, 0, .1);
            transition: width .2s cubic-bezier(0, 0, 0, .1);
        }
    }

    &--label-before {
        input:checked + label {
            // The toggle "dot".
            // Positioned at the right end of the track (enabled), at the right end of the element.
            &:after {
                right: 0;
            }
        }
    }

    &--label-after {
        input:checked + label {
            // The toggle "dot".
            // Positioned at the right end of the track (enabled), at the left end of the element.
            &:after {
                left: 54px - 30px
            }
        }
    }

    &--disabled {
        cursor: not-allowed;

        input,
        label {
            pointer-events: none;
        }

        input + label {
            // Add a lock icon in the toggle dot.
            &:after {
                content: @fa-var-lock;
                display: flex;
                align-items: center;
                justify-content: center;
                font-family: @fa-style-family;
                font-size: 12px;
                color: @dt-dark-grey;
                font-weight: bold;
            }
        }
    }

    &--non-bold {
        label {
            font-weight: normal;
        }
    }

    // The small version is 40x20px.
    &--sm {
        height: 20px;

        input + label {
            height: 20px;
            min-width: 40px;

            // The track that the toggle "dot" travels within.
            &:before {
                height: 20px;
                width: 40px;
            }

            // The toggle "dot".
            &:after {
                height: 18px;
                width: 18px;
                right: ~"calc(40px - 18px - 1px);";
            }

            &:not(:empty) {
                // Add padding between the label and toggle.
                // This is the width of the toggle plus a gutter.
                padding-right: 40px + @half-gutter-width;
            }
        }

        &.toggle--disabled {
            input + label {
                // Lock icon on the toggle dot. Make it smaller.
                &:after {
                    font-size: 10px;
                }
            }
        }
    }
}

</style>
